import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { history } from 'store';
import {
  change,
  formValueSelector,
  reduxForm,
  arrayRemove /*SubmissionError*/,
} from 'redux-form';
import {
  Button,
  Datepicker,
  Input,
  Modal,
  ToggleButton,
} from 'common/components';
import { convertJsonToObj } from 'common/utils';
import {
  ContractsForm,
  FieldForm,
  StorageForm,
  StoragesForm,
  FertilizersForm,
  ChemicalsForm,
} from './';
import {
  CropField,
  DestField,
  SourceField,
  ScoutingFields,
  TypeField,
  SeedingFields,
} from '../fields';
import { FertilizerForm, ChemicalForm } from 'containers';
import { maxLength, required } from 'common/form-validators';
import { trim } from 'common/normalizers';
import {
  ACTIVITY_SIZE,
  ACTIVITY_TYPES,
  ACTIVITY_NOTES,
  CLEAR_MARKER,
  FIELD_DISPLAY_TOGGLE,
} from 'common/constants';
import get from 'lodash.get';

class ActivityForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      fields: '',
      other: props.otherName && props.otherName.value, // single source other field
      focused: null,
      max: {},
      fertilizerModal: false,
      chemicalModal: false,
      activityFertilizer: {},
      activityChemical: {},
    };
  }

  notesMax = value => maxLength(value, ACTIVITY_SIZE.MAX_NOTES);
  hasValue = value => value && value.filter(val => val).length;

  // control chemical or fertilizer modal base on mode
  toggleModal = (obj, fieldname, mode) => {
    const STATE_BY_MODE = {
      chemical: {
        nextState: { chemicalModal: !this.state.chemicalModal },
        stateTag: 'activityChemical',
      },
      fertilizer: {
        nextState: { fertilizerModal: !this.state.fertilizerModal },
        stateTag: 'activityFertilizer',
      },
    };
    const { nextState, stateTag } = STATE_BY_MODE[mode];
    let state = nextState;
    // makesure the obj is stringified json-like value
    if (obj && typeof obj === 'string') {
      state = { ...state };
      state[stateTag] = { ...JSON.parse(obj), fieldname };
    }

    this.setState(state);
  };

  // when mark as done clicked, populate the status (have to trigger redux action instead of passing from initialvalues to avoid pristine)
  componentDidMount() {
    const { changeValue, initialValues, extra } = this.props;
    if (initialValues && !initialValues.status && extra === 'done') {
      changeValue('status', true);
    }
  }

  componentDidUpdate(prevProps) {
    const { changeValue, initialValues, extra } = this.props;
    if (
      !prevProps.initialValues &&
      initialValues &&
      !initialValues.status &&
      extra === 'done'
    ) {
      changeValue('status', true);
    }
  }

  // set this on rendering ActionList to use remotely from PillList
  handleFieldsObj = fields => {
    this.setState({ fields });
  };

  handleRemoveAction = ({ key, value }) => {
    const { changeValue, clearMarker } = this.props;
    changeValue(key, '');
    changeValue('amount', '');
    clearMarker();

    if (value === 'custom') {
      let obj = { ...this.state };
      obj[key] = false;
      this.setState(obj);
    }

    if (key === 'fieldName') {
      changeValue('location', '');
    }
  };

  render() {
    const {
      id,
      changeValue,
      clearMarker,
      contracts,
      crops,
      error,
      fieldName,
      goBack,
      goToOption,
      handleSubmit,
      otherName,
      pristine,
      reset,
      season,
      selectedContracts,
      selectedCrop,
      selectedFertilizers,
      selectedChemicals,
      selectedStorage,
      selectedStorages,
      selectedOthers,
      selectedType,
      selectedYield,
      storages,
      submitting,
      toggleFieldView,
      valid,
      arrayRemove,
      plantedArea,
    } = this.props;
    // static - currently specified in spreadsheet
    const options = {
      SCOUTING: {
        source: [
          {
            label: 'Field',
            action: () => {
              goToOption(FieldForm);
            },
            result: fieldName,
            hide: fieldName.value,
            amount: false,
          },
        ],
      },
      FERTILIZING: {
        target: crops,
        source: [
          {
            label: 'Field',
            action: () => {
              goToOption(FieldForm);
            },
            result: fieldName,
            disabled: !selectedCrop,
            hide: fieldName.value,
            amount: false,
          },
        ],
        destTitle: 'Fertilizer',
        destRequired: false,
        dest: [
          {
            label: 'Fertilizer',
            action: () => {
              goToOption(FertilizersForm);
            },
            result: selectedFertilizers,
            disabled: !fieldName.value,
          },
        ],
      },
      SPRAYING: {
        target: crops,
        source: [
          {
            label: 'Field',
            action: () => {
              goToOption(FieldForm);
            },
            result: fieldName,
            disabled: !selectedCrop,
            hide: fieldName.value,
            amount: false,
          },
        ],
        destTitle: 'Chemical',
        destRequired: false,
        dest: [
          {
            label: 'Chemical',
            action: () => {
              goToOption(ChemicalsForm);
            },
            result: selectedChemicals,
            disabled: !fieldName.value,
          },
        ],
      },
      SEEDING: {
        target: crops,
        source: [
          {
            label: 'Field',
            action: () => {
              goToOption(FieldForm);
            },
            result: fieldName,
            hide: fieldName.value,
            amount: false,
          },
        ],
        destTitle: 'Fertilizer',
        destRequired: false,
        dest: [
          {
            label: 'Fertilizer',
            action: () => {
              goToOption(FertilizersForm);
            },
            result: selectedFertilizers,
            disabled: !fieldName.value,
          },
        ],
      },
      HARVESTING: {
        target: crops,
        sourceTitle: 'Source',
        source: [
          {
            label: 'Field',
            action: () => {
              goToOption(FieldForm);
            },
            result: fieldName,
            disabled: !selectedCrop,
            hide: fieldName.value,
            amount: 'yield',
          },
        ],
        destTitle: 'Destination',
        destRequired: true,
        dest: [
          {
            label: 'Storage',
            action: () => {
              goToOption(StoragesForm);
            },
            result: selectedStorages,
            disabled: !fieldName.value || !storages || !storages.length,
          },
          {
            label: 'Contract',
            action: () => {
              goToOption(ContractsForm);
            },
            result: selectedContracts,
            disabled: !fieldName.value || !contracts || !contracts.length,
          },
          {
            label: 'Other',
            action: () => {
              this.state.fields.push({});
            },
            // 'custom' is used to use FieldArray
            result: { key: 'others', value: 'custom' },
            disabled: !fieldName.value,
          },
        ],
      },
      STORAGE: {
        target: storages,
        sourceTitle: 'Source',
        source: [
          {
            label: 'Storage',
            action: () => {
              goToOption(StorageForm);
            },
            result: selectedStorage,
            disabled: !selectedCrop || !storages || !storages.length,
            hide: selectedStorage.value || this.state.other,
            amount: true,
          },
          {
            label: 'Other',
            action: () => {
              this.setState({ ...this.state, other: true });
            },
            result: { key: 'other', value: 'custom' },
            disabled: !selectedCrop,
            hide: selectedStorage.value || this.state.other,
            amount: true,
          },
        ],
        destTitle: 'Destination',
        destRequired: true,
        dest: [
          {
            label: 'Storage',
            action: () => {
              goToOption(StoragesForm);
            },
            result: selectedStorages,
            disabled:
              (!selectedStorage.value && !otherName.value) ||
              !storages ||
              !storages.length,
          },
          {
            label: 'Other',
            action: () => {
              this.state.fields.push({});
            },
            // 'custom' is used to use FieldArray
            result: { key: 'others', value: 'custom' },
            disabled: !selectedStorage.value && !otherName.value,
          },
        ],
      },
      DELIVERY: {
        target: contracts,
        sourceTitle: 'Source',
        source: [
          {
            label: 'Storage',
            action: () => {
              goToOption(StorageForm);
            },
            result: selectedStorage,
            disabled: !selectedCrop || !storages || !storages.length,
            hide: selectedStorage.value || this.state.other,
            amount: true,
          },
          {
            label: 'Other',
            action: () => {
              this.setState({ ...this.state, other: true });
            },
            result: { key: 'other', value: 'custom' },
            disabled: !selectedCrop,
            hide: selectedStorage.value || this.state.other,
            amount: true,
          },
        ],
        destTitle: 'Deliver To',
        destRequired: true,
        dest: [
          {
            label: 'Contract',
            action: () => {
              goToOption(ContractsForm);
            },
            result: selectedContracts,
            disabled: !selectedCrop || !contracts || !contracts.length,
            hide: this.hasValue(selectedContracts.value),
          },
        ],
      },
      OTHER: {
        source: [
          {
            label: 'Field',
            action: () => {
              goToOption(FieldForm);
            },
            result: fieldName,
            hide: fieldName.value,
            amount: false,
          },
        ],
      },
    };

    // use first source for now
    const mandatoryPill = !!(
      options[selectedType].source &&
      options[selectedType].source[0] &&
      options[selectedType].source[0].hide
    );

    const isDestRequired = options[selectedType].destRequired;
    const hasDestination =
      get(selectedStorages, 'value.length') ||
      get(selectedContracts, 'value.length') ||
      get(selectedOthers, 'value.length');

    const isEdit = id !== 'add';
    // disable status for harvesting, delivery, seeding, and storage activity
    const statusDisabled = [
      ACTIVITY_TYPES.HARVESTING,
      ACTIVITY_TYPES.DELIVERY,
      ACTIVITY_TYPES.SEEDING,
      ACTIVITY_TYPES.STORAGE,
    ].includes(selectedType);

    const showVariety =
      selectedType !== ACTIVITY_TYPES.STORAGE &&
      selectedType !== ACTIVITY_TYPES.DELIVERY; // hide variety for storage and delivery activity

    const extraCropOptions = [];
    if (selectedType === ACTIVITY_TYPES.FERTILIZING) {
      extraCropOptions.push({
        label: 'Empty Fields',
        value: JSON.stringify({ name: null }),
      });
    }

    // crop options (dropdown list) is rendered based on source's crop object
    // if the source is deleted, it would render empty selection even value is provided.
    // thus, extra crop option is pushed to `cropOption` array when it's in edit mode
    if (isEdit && convertJsonToObj(selectedCrop)) {
      extraCropOptions.push({
        label: convertJsonToObj(selectedCrop).name,
        value: selectedCrop,
      });
    }

    return (
      <Fragment>
        <form onSubmit={handleSubmit}>
          <small className={'text--info'}>* Required</small>
          <fieldset className={'field__group'}>
            <Datepicker
              name="activityDate"
              disabled={submitting}
              title="Date"
              season={season}
              validate={[required]}
              defaultToToday={!isEdit}
            />

            {!statusDisabled && (
              <ToggleButton
                name="status"
                disabled={submitting}
                defaultState={false}
                title="Status"
                leftButton={{ text: 'Planned' }}
                rightButton={{ text: 'Done' }}
              />
            )}

            {[
              ACTIVITY_TYPES.OTHER,
              ACTIVITY_TYPES.SCOUTING,
              ACTIVITY_TYPES.SEEDING,
            ].indexOf(selectedType) < 0 && (
              <CropField
                showVariety={showVariety}
                submitting={submitting}
                changeValue={changeValue}
                clearMarker={clearMarker}
                disabled={isEdit} // disable crop selection for update mode
                options={options[selectedType]}
                extra={extraCropOptions}
              />
            )}

            {selectedType === ACTIVITY_TYPES.OTHER && (
              <TypeField submitting={submitting} disabled={isEdit} />
            )}

            <SourceField
              submitting={submitting}
              changeValue={changeValue}
              fieldName={fieldName}
              selectedYield={selectedYield}
              options={options[selectedType]}
              handleRemoveAction={this.handleRemoveAction}
              other={this.state.other}
              selectedType={selectedType}
              id={id}
              isEdit={isEdit}
            />

            {selectedType === ACTIVITY_TYPES.SEEDING && (
              <SeedingFields
                submitting={submitting}
                plantedArea={plantedArea}
                selectedCrop={selectedCrop}
                crops={crops}
                isEdit={isEdit}
              />
            )}

            <DestField
              submitting={submitting}
              options={options[selectedType]}
              toggleModal={this.toggleModal}
              handleFieldsObj={this.handleFieldsObj}
              arrayRemove={arrayRemove}
              isEdit={isEdit}
            />

            {selectedType === ACTIVITY_TYPES.SCOUTING && (
              <ScoutingFields
                submitting={submitting}
                changeValue={changeValue}
                toggleFieldView={toggleFieldView}
              />
            )}

            {selectedType === ACTIVITY_TYPES.SPRAYING && (
              <Input
                name="sprayingRate"
                disabled={submitting}
                title="SPRAYING RATE"
                hintText="in l/acre or kg/acre"
                validateOnChange
                normalizeOnBlur={trim}
              />
            )}

            <Input
              name="notes"
              disabled={submitting}
              title="Notes"
              hintText={ACTIVITY_NOTES[selectedType]}
              validate={[this.notesMax]}
              normalizeOnBlur={trim}
            />
          </fieldset>

          {error && <p className="text--error">{error}</p>}

          <div className={'field__group'}>
            <fieldset className={'field text--center'}>
              <Button
                type="submit"
                className="btn btn--primary gap-top-small"
                disabled={
                  !valid ||
                  pristine ||
                  submitting ||
                  !mandatoryPill ||
                  (isDestRequired && !hasDestination)
                }
              >
                Save
              </Button>
            </fieldset>

            <fieldset className={'field text--center'}>
              <Button
                type="button"
                className="btn"
                onClick={() => {
                  reset();
                  goBack();
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
            </fieldset>
          </div>
        </form>

        {this.state.fertilizerModal && (
          <Modal
            enableTopRightClose
            theme={['narrow']}
            onToggle={() => this.toggleModal(null, null, 'fertilizer')}
          >
            <FertilizerForm
              closeForm={() => this.toggleModal(null, null, 'fertilizer')}
              activityFertilizer={this.state.activityFertilizer}
              changeValue={changeValue}
            />
          </Modal>
        )}
        {this.state.chemicalModal && (
          <Modal
            enableTopRightClose
            theme={['narrow']}
            onToggle={() => this.toggleModal(null, null, 'chemical')}
          >
            <ChemicalForm
              closeForm={() => this.toggleModal(null, null, 'chemical')}
              activityChemical={this.state.activityChemical}
              changeValue={changeValue}
            />
          </Modal>
        )}
      </Fragment>
    );
  }
}

ActivityForm.propTypes = {
  // from redux form
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  season: PropTypes.number.isRequired,
  reset: PropTypes.func.isRequired,

  // field values
  fieldName: PropTypes.object,
  otherName: PropTypes.object,
  selectedCrop: PropTypes.string,
  selectedStorage: PropTypes.object,
  selectedStorages: PropTypes.object,
  selectedOthers: PropTypes.object,
  selectedContracts: PropTypes.object,
  selectedFertilizers: PropTypes.object,
  selectedChemicals: PropTypes.object,
  selectedYield: PropTypes.string,
  plantedArea: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // dispatch
  changeValue: PropTypes.func.isRequired,
  toggleFieldView: PropTypes.func.isRequired,
  clearMarker: PropTypes.func.isRequired,
  arrayRemove: PropTypes.func.isRequired,
  // setErrors: PropTypes.func.isRequired,

  // manually added
  onSubmit: PropTypes.func.isRequired,
  goToOption: PropTypes.func.isRequired,
  selectedType: PropTypes.string,
  goBack: PropTypes.func.isRequired,
  crops: PropTypes.arrayOf(PropTypes.object),
  storages: PropTypes.arrayOf(PropTypes.object),
  contracts: PropTypes.arrayOf(PropTypes.object),
  location: PropTypes.object,
  id: PropTypes.string.isRequired,
  extra: PropTypes.string,
  initialValues: PropTypes.object,
};

const formSettings = {
  form: 'activityform',
  enableReinitialize: true,
  destroyOnUnmount: true,
};
const field = formValueSelector(formSettings.form);
const mapState = (state, { initialValues }) => ({
  // used to render fields
  fieldName: {
    key: 'fieldName',
    value: field(state, 'fieldName'),
  },
  otherName: {
    key: 'other',
    value: field(state, 'other'),
  },
  selectedCrop: field(state, 'crop'),
  selectedContracts: {
    key: 'contracts',
    value: field(state, 'contracts'),
  },
  selectedStorage: {
    key: 'storage',
    value: field(state, 'storage'),
  },
  selectedStorages: {
    key: 'storages',
    value: field(state, 'storages'),
  },
  selectedOthers: {
    key: 'others',
    value: field(state, 'others'),
  },
  selectedFertilizers: {
    key: 'fertilizers',
    value: field(state, 'fertilizers'),
  },
  selectedChemicals: {
    key: 'chemicals',
    value: field(state, 'chemicals'),
  },
  selectedYield: field(state, 'yield') || '0',
  season: state.settings.season,
  plantedArea: state.settings.maxPlantingArea,
  goBack: () => {
    history.goBack();
  },
  initialValues,
});

const mapDispatch = dispatch => ({
  changeValue: (fieldname, value) =>
    dispatch(change(formSettings.form, fieldname, value)),
  arrayRemove: (fieldname, index) =>
    dispatch(arrayRemove(formSettings.form, fieldname, index)),
  // setErrors: (fieldname, value) => actions.setErrors(`${formSettings.form}.${fieldname}`, value),
  toggleFieldView: () => dispatch({ type: FIELD_DISPLAY_TOGGLE }),
  clearMarker: () => dispatch({ type: CLEAR_MARKER, payload: true }),
});

export default compose(
  reduxForm(formSettings),
  connect(
    mapState,
    mapDispatch
  )
)(ActivityForm);



// WEBPACK FOOTER //
// ./src/containers/ActivityForm/forms/Activity.js